import {
  BSC_CHAIN_ID,
  SALE_CHAIN_DECIMALS,
  SALE_TOKEN_UNIT,
} from "./sale-flow.constants";
import {
  getSupportedNetworkChainIds,
  isSupportedNetworkChainId,
  normalizeChainId,
} from "app/utils/network-chain.utils";

export { normalizeChainId };

export const getSupportedChainIds = (
  supportedNetworks: Parameters<typeof getSupportedNetworkChainIds>[0] = []
): number[] => {
  return getSupportedNetworkChainIds(supportedNetworks);
};

export const isSupportedChainId = (
  chainId: number | null,
  supportedChainIds: number[]
): chainId is number => {
  return isSupportedNetworkChainId(chainId, supportedChainIds);
};

export const getStablecoinDecimals = (chainId: number): number => {
  return chainId === BSC_CHAIN_ID
    ? SALE_CHAIN_DECIMALS.bscMainnet
    : SALE_CHAIN_DECIMALS.defaultStablecoin;
};

type EffectiveMinAllocationParams = {
  dbMinAllocationUsd: number;
  contractMinPurchaseAmount?: bigint | null;
  tokenPriceUsd: number;
};

export const getEffectiveMinAllocationUsd = ({
  dbMinAllocationUsd,
  contractMinPurchaseAmount,
  tokenPriceUsd,
}: EffectiveMinAllocationParams): number => {
  if (!contractMinPurchaseAmount || tokenPriceUsd <= 0) {
    return dbMinAllocationUsd;
  }

  const contractMinTokens = Number(contractMinPurchaseAmount) / SALE_TOKEN_UNIT;

  if (!Number.isFinite(contractMinTokens)) {
    return dbMinAllocationUsd;
  }

  return Math.max(dbMinAllocationUsd, contractMinTokens * tokenPriceUsd);
};

const EXPLORER_TX_BASE: Record<number, string> = {
  1: "https://etherscan.io/tx/",
  56: "https://bscscan.com/tx/",
  137: "https://polygonscan.com/tx/",
  8453: "https://basescan.org/tx/",
  42161: "https://arbiscan.io/tx/",
  59144: "https://lineascan.build/tx/",
};

export const getExplorerTxUrl = (
  chainId?: number | null,
  hash?: string | null
): string | null => {
  if (!chainId || !hash) return null;
  const base = EXPLORER_TX_BASE[chainId];
  return base ? `${base}${hash}` : null;
};

// Message for a transaction that was mined but reverted on-chain. wagmi's
// useWaitForTransactionReceipt resolves successfully for reverted txs, so we
// must surface the real reason instead of a generic "not found".
export const buildSaleRevertedError = (
  hash?: string | null,
  chainId?: number | null
): string => {
  const url = getExplorerTxUrl(chainId, hash);
  return `Your purchase transaction failed (reverted on-chain) — most likely the wallet didn't have enough balance to cover the amount. No funds were taken, only the network fee.${
    url ? ` View it: ${url}` : ""
  }`;
};

// Message for when the receipt itself could not be read (RPC/timeout), as
// opposed to a tx that does not exist.
export const buildSaleReceiptError = (
  hash?: string | null,
  chainId?: number | null
): string => {
  const url = getExplorerTxUrl(chainId, hash);
  return url
    ? `We couldn't confirm your transaction on the network. Please check its status on the explorer: ${url}`
    : "We couldn't confirm your transaction on the network. Please check your wallet and try again.";
};
